/**
 * Financial reports — revenue + payment summary + invoices.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../lib/api';
import Sparkline from './components/Sparkline';

const fmtINR = (paise) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
    .format((paise || 0) / 100);

export default function FinancialReports() {
  const api = useApi();
  const [granularity, setGranularity] = useState('day');
  const [revenue, setRevenue] = useState(null);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, s, i] = await Promise.all([
        api.adminRevenue({ granularity }),
        api.adminPaymentSummary(),
        api.adminInvoices({ limit: 50 }),
      ]);
      setRevenue(r); setSummary(s); setInvoices(i.invoices || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  }, [api, granularity]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="admin-banner error">{error}</div>;
  if (!revenue || !summary) return <div style={{ color: '#687078' }}>Loading…</div>;

  const series = (revenue.series || []).map((p) => ({
    label: granularity === 'month' ? p.bucket : p.bucket.slice(5),
    value: p.revenue_paise,
  }));

  return (
    <>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Financial</h1>
          <p className="admin-page-subtitle">Revenue, payment health, and invoice summary.</p>
        </div>
        <div className="admin-header-actions">
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Date range</span>
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value)}
            style={{
              font: 'inherit', fontSize: 13, padding: '6px 28px 6px 12px', border: '1px solid #d1d5db',
              borderRadius: 8, appearance: 'none', background: `white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 8px center`,
              color: '#374151', cursor: 'pointer',
            }}
          >
            <option value="day">Daily</option>
            <option value="month">Monthly</option>
          </select>
          <button className="btn-primary" onClick={load} style={{ gap: 6 }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Revenue</h2>
          <select value={granularity} onChange={(e) => setGranularity(e.target.value)}
            style={{
              font: 'inherit', fontSize: 13, padding: '5px 28px 5px 10px', border: '1px solid #d1d5db',
              borderRadius: 8, appearance: 'none', background: `white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 8px center`,
              color: '#374151', cursor: 'pointer',
            }}>
            <option value="day">Daily</option>
            <option value="month">Monthly</option>
          </select>
        </div>
        {/* Revenue KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 20 }}>
          {[
            { label: 'Total revenue',  value: fmtINR(revenue.totals?.revenue_paise), sub: `↑ 0% vs previous period`, color: '#16a34a' },
            { label: 'Payments',       value: revenue.totals?.payments || 0,         sub: `${summary.totals.success} successful` },
            { label: 'Refunds',        value: 0,                                      sub: '0 refunds' },
            { label: 'Net revenue',    value: fmtINR(revenue.totals?.revenue_paise), sub: `across ${revenue.totals?.payments || 0} payments` },
          ].map(k => (
            <div key={k.label} style={{ padding: '14px 0', borderRight: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600, marginBottom: 6 }}>{k.label}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#0f1c2e', lineHeight: 1, marginBottom: 4 }}>{k.value}</div>
              {k.sub && <div style={{ fontSize: 12, color: k.color || '#9ca3af' }}>{k.sub}</div>}
            </div>
          ))}
        </div>
        <Sparkline data={series} valueFormat={(v) => fmtINR(v)} />
        <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 8 }}>
          Total: <strong>{fmtINR(revenue.totals?.revenue_paise)}</strong> across {revenue.totals?.payments || 0} payments.
        </p>
      </div>

      <div className="admin-card-grid" style={{ marginBottom: 16 }}>
        <div className="admin-card" style={{ marginBottom: 0 }}>
          <h2 style={{ marginBottom: 16 }}>Payment status (window)</h2>
          <div className="admin-payment-kpi-grid" style={{ marginBottom: 16 }}>
            {[
              { label: 'Successful',    value: summary.totals.success,           icon: '✅', bg: '#f0fdf4' },
              { label: 'Pending recon', value: summary.pendingReconciliation,     icon: '🕐', bg: '#fff7ed' },
              { label: 'Failed',        value: summary.totals.failed,            icon: '❌', bg: '#fef2f2' },
            ].map(k => (
              <div key={k.label} className="admin-payment-kpi">
                <div className="admin-payment-kpi-icon" style={{ background: k.bg, fontSize: 15 }}>{k.icon}</div>
                <div>
                  <div className="admin-payment-kpi-label">{k.label}</div>
                  <div className="admin-payment-kpi-value">{k.value}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
            Success rate: <strong style={{ color: '#374151' }}>{summary.totals.successRate != null ? Math.round(summary.totals.successRate * 100) + '%' : '—'}</strong>
            {' · '}Pending reconciliation: <strong style={{ color: '#374151' }}>{summary.pendingReconciliation}</strong>
          </div>
        </div>

        <div className="admin-card" style={{ marginBottom: 0 }}>
          <h2 style={{ marginBottom: 16 }}>Reconciliation flag distribution</h2>
          <table className="admin-table">
            <thead><tr><th>Flag</th><th>Count</th></tr></thead>
            <tbody>
              {(summary.reconciliationFlags || []).map((r) => (
                <tr key={r.flag}><td>{r.flag}</td><td>{r.n}</td></tr>
              ))}
              {(!summary.reconciliationFlags || summary.reconciliationFlags.length === 0) && (
                <tr><td colSpan="2" className="admin-empty">No data.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Recent invoices</h2>
        </div>
        {invoices.length === 0 ? <div className="admin-empty">No invoices yet.<br/><span style={{ fontSize: 12 }}>Invoices will appear here once available.</span></div> : (
          <div className="admin-table-scroll"><table className="admin-table">
            <thead>
              <tr><th>SL. NO</th><th>Invoice</th><th>App</th><th>Amount</th><th>Status</th><th>Issued</th></tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td>{i.id}</td>
                  <td><code>{i.invoice_number}</code></td>
                  <td>{i.app_name || '—'}</td>
                  <td>{fmtINR(i.amount_paise + (i.tax_paise || 0))}</td>
                  <td><span className={`admin-status-pill ${i.status === 'paid' ? 'ok' : i.status === 'void' ? 'failed' : 'partial'}`}>{i.status ? i.status.charAt(0).toUpperCase() + i.status.slice(1) : '—'}</span></td>
                  <td>{i.issued_at || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </>
  );
}
